import { Delete, Edit, Plus, Save, User } from "lucide-react";
import React, { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { ServiceData } from "../../../Services/Service";
import ConfirmDeleteDialog from "../../../View/ConfirDeleteDialog";
import { toast } from "react-toastify";


type User = {
  id: number;
  nom: string;
  firstname: string;
  magasin: string;
  email: string;
  roles: string[];
};

type Props = {
  users: User[];
};

const regex = {
  name: /^[A-Za-zÀ-ÿ\s'-]{2,}$/,
  magasin: /^[A-Za-z0-9\s\-']{2,}$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  password: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
};

const roleLabels: { [key: string]: string } = {
  ROLE_ADMIN: "Administrateur",
  ROLE_MAGASIN: "Magasin",
  ROLE_USER: "Utilisateur simple",
  ROLE_MAGASIN_LOIN:"Magasin loin"
};

const ROLES = ["ROLE_ADMIN", "ROLE_MAGASIN","ROLE_MAGASIN_LOIN"];

const UserTables: React.FC<Props> = ({ users }) => {
  const [search, setSearch] = useState('');
  const [userList, setUserList] = useState<User[]>(users);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [userToDelete,setUserToDelete]=useState<User | null>(null);

  const [form, setForm] = useState({
    nom: '',
    firstname: '',
    email: '',
    magasin: '',
    password: '',
    confirmPwd: '',
    roles: [] as string[],
    newpassword: '',
  });

  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };


  const openCreateModal = () => {
    setIsEditMode(false);
    setIsModalOpen(true);
    setForm({
      nom: '',
      firstname: '',
      email: '',
      magasin: '',
      password: '',
      confirmPwd: '',
      roles: [],
      newpassword: ''
    });
    setError(null);
  };

  const openEditModal = (user: User) => {
    setIsEditMode(true);
    setIsModalOpen(true);
    setSelectedUser(user);
    setForm({
      nom: user.nom,
      firstname: user.firstname,
      email: user.email,
      magasin: user.magasin,
      password: '',
      confirmPwd: '',
      roles: user.roles,
      newpassword: ''
    });
    setError(null);
  };

  const DeleteUser=async()=>{
    if(!userToDelete) return
    try{
      await ServiceData(`/api/admin/deleteuser/${userToDelete.id}`,'DELETE');
      setUserList(prev => prev.filter(u => u.id !== userToDelete.id));
      setUserToDelete(null);
    }catch(err:any){
      toast.error(err.message || "Erreur lors de la suppression");
    }
  }

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!regex.name.test(form.firstname)) return setError("Prénom invalide."), false;
    if (!regex.magasin.test(form.magasin)) return setError("Magasin invalide."), false;
    if (!isEditMode) {
      if (!regex.name.test(form.nom)) return setError("Nom invalide."), false;
      if (!regex.email.test(form.email)) return setError("Email invalide."), false;
      if (!regex.password.test(form.password)) return setError("Mot de passe faible."), false;
      if (form.password !== form.confirmPwd) return setError("Les mots de passe ne correspondent pas."), false;
    } else if (form.newpassword || form.confirmPwd) {
      if (!regex.password.test(form.newpassword)) return setError("Mot de passe faible."), false;
      if (form.newpassword !== form.confirmPwd) return setError("Les mots de passe ne correspondent pas."), false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (isEditMode && selectedUser) {
        const updatePayload = {
          firstname: form.firstname,
          magasin: form.magasin,
          newpassword: form.newpassword,
          roles: form.roles
        };

        await ServiceData(`/api/admin/editusers/${selectedUser.id}`, 'PUT', updatePayload);

        setUserList(prev => prev.map(u => u.id === selectedUser.id ? { ...u, ...updatePayload } : u));
      } else {
        const createPayload = {
          nom: form.nom,
          firstname: form.firstname,
          email: form.email,
          magasin: form.magasin,
          password: form.password,
          roles: form.roles.length > 0 ? form.roles : ['ROLE_USER']
        };

        await ServiceData(`/api/admin/register`, 'POST', createPayload);
      }

      closeModal();
    } catch (err: any) {
      setError(err.message || "Erreur serveur.");
    }
  };

  const filterUsers = useMemo(() =>
    userList.filter(user =>
      `${user.nom} ${user.firstname} ${user.email} ${user.roles.join(' ')}`.toLowerCase().includes(search.toLowerCase())
    ), [search, userList]);

  const columns: TableColumn<User>[] = [
    { name: 'Nom', selector: row => row.nom, sortable: true },
    { name: 'Prénom', selector: row => row.firstname, sortable: true },
    { name: 'Magasin', selector: row => row.magasin, sortable: true },
    { name: 'Email', selector: row => row.email },
    {
      name: 'Roles', selector: row =>
        row.roles.map(role => roleLabels[role] || role).join(', ')
    },
    {
      name: 'Actions',
      cell: row => (
        <div>
          <button className="btn btn-sm btn-outline-primary m-2" onClick={() => openEditModal(row)}>
            <Edit />
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={()=>setUserToDelete(row)}>
            <Delete/>
          </button>
        </div>

      )
    }
  ];

  // const toggleRole = (role: string) => {
  //   if (role === "ROLE_ADMIN") {
  //     setForm((prev) => ({
  //       ...prev,
  //       roles: prev.roles.includes(role)
  //         ? prev.roles.filter((r) => r !== role)
  //         : [...prev.roles, role],
  //     }));
  //   } else if (role === "ROLE_MAGASIN" || role === "ROLE_MAGASIN_LOIN") {
  //     setForm((prev) => ({
  //       ...prev,
  //       roles: [
  //         ...prev.roles.filter(
  //           (r) => r !== "ROLE_MAGASIN" && r !== "ROLE_MAGASIN_LOIN"
  //         ),
  //         role,
  //       ],
  //     }));
  //   }
  // };
  //Mettre simp^le role
  const toggleRole = (role: string) => {
    setForm((prev) => {
      // Si le rôle est déjà sélectionné => on l'enlève
      if (prev.roles.includes(role)) {
        return { ...prev, roles: [] };
      }

      // Sinon on remplace le tableau par le seul rôle sélectionné
      return { ...prev, roles: [role] };
    });
  };


  const renderModal = () => (
    <div className="modal show fade d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header" style={{backgroundColor: 'green',color: 'white',alignItems: 'center'}}>
              <h5 className="modal-title">{isEditMode ? `Modifier ${form.firstname}` : 'Créer un utilisateur'}</h5>
              <button type="button" className="btn-close" onClick={closeModal}></button>
            </div>
            <div className="modal-body">
              {error && <p className="text-danger">{error}</p>}
              {!isEditMode && (
                <>
                  <input className="form-control mb-2" placeholder="Nom" value={form.nom} onChange={e => handleInputChange('nom', e.target.value)} />
                </>
              )}

              <input className="form-control mb-2" placeholder="Prénom" value={form.firstname} onChange={e => handleInputChange('firstname', e.target.value)} />
              {!isEditMode && (
                <>
                  <input className="form-control mb-2" placeholder="Email" value={form.email} onChange={e => handleInputChange('email', e.target.value)} />
                </>
              )}
              <input className="form-control mb-2" placeholder="Magasin" value={form.magasin} onChange={e => handleInputChange('magasin', e.target.value)} />
              <input className="form-control mb-2" type="password" placeholder={isEditMode ? "Nouveau mot de passe" : "Mot de passe"} value={isEditMode ? form.newpassword : form.password} onChange={e => handleInputChange(isEditMode ? 'newpassword' : 'password', e.target.value)} />
              <input className="form-control mb-2" type="password" placeholder="Confirmation" value={form.confirmPwd} onChange={e => handleInputChange('confirmPwd', e.target.value)} />
              <div className="mb-3">
                <label>Rôles :</label>
                {ROLES.map(role => (
                  <div key={role} className="form-check">
                    <input type="checkbox" className="form-check-input" id={role} checked={form.roles.includes(role)} onChange={() => toggleRole(role)} />
                    <label className="form-check-label" htmlFor={role}>{roleLabels[role]}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-dark" type="submit"><Save /> Enregistrer</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
  <div className="user-container">
    <div className="top-bar">
      <input
        type="text"
        className="search-input"
        placeholder="🔍 Rechercher un utilisateur..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <button className="create-btn" onClick={openCreateModal}>
        <Plus size={16} /> Créer utilisateur
      </button>
    </div>

    <div className="table-container">
      <DataTable
        title="Liste des utilisateurs"
        columns={columns}
        data={filterUsers}
        pagination
        highlightOnHover
        striped
      />
    </div>

    {isModalOpen && renderModal()}

    {userToDelete && (
      <ConfirmDeleteDialog
        user={userToDelete}
        error={error}
        onCancel={() => setUserToDelete(null)}
        onConfirm={DeleteUser}
      />
    )}
  </div>

  );
};

export default UserTables;
